/**
 * One trade.
 *
 * Two things this screen has to get right, because they are the whole point
 * of escrow:
 *
 *   1. You confirm the CONTENTS you are looking at, not the trade. The hash
 *      you saw goes back with the confirmation, and the server refuses it if
 *      anything changed — so a last-second swap surfaces as a refusal rather
 *      than as a completed trade you did not agree to.
 *   2. Any edit clears EVERY confirmation, including the other side's. The
 *      screen says so before you edit, so withdrawing an item is never a
 *      surprise to the person who already agreed.
 */
import { useState, type ReactNode } from 'react';
import type { Instance, Trade } from '@tomo/contracts';
import { Shell } from '../components/Shell.jsx';
import { Badge, Button, Failed, Spinner } from '../components/primitives.jsx';
import { ApiError } from '../lib/api.js';
import { instances, trades } from '../lib/tomo.js';
import { useAsync } from '../useAsync.js';
import { useSession } from '../Session.jsx';
import { navigate, hrefFor } from '../lib/router.js';
import { Status } from './Trades.jsx';

export function TradeDetail({ id }: { id: string }): ReactNode {
  const session = useSession();
  const [result, retry] = useAsync(
    async (signal) => ({
      trade: await trades.get(id, signal),
      mine: await instances.mine(undefined, signal),
    }),
    [id]
  );

  if (result.state === 'loading') {
    return <Shell><Spinner label="Loading the trade…" /></Shell>;
  }
  if (result.state === 'failed') {
    return (
      <Shell>
        <Failed
          message={result.error.status === 404 ? 'No such trade.' : 'Could not load the trade.'}
          {...(result.error.status === 404 ? {} : { onRetry: retry })}
        />
      </Shell>
    );
  }

  return (
    <Detail
      trade={result.value.trade}
      inventory={result.value.mine}
      me={session.user.uid}
      onChanged={retry}
    />
  );
}

function Detail(
  { trade, inventory, me, onChanged }:
  { trade: Trade; inventory: Instance[]; me: string; onChanged: () => void }
): ReactNode {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const mySide = trade.sides.find((s) => s.uid === me);
  const others = trade.sides.filter((s) => s.uid !== me);
  const theirUid = trade.participants.find((p) => p !== me) ?? '';
  const done = trade.status === 'settled' || trade.status === 'cancelled';

  // Revealed, unlocked, and mine. A wrapped item cannot be traded: trading
  // the anticipation is a different product from trading the item.
  const offerable = inventory.filter(
    (i) => i.reveal === 'revealed' && (i.custody === 'owner' || offered(i.id))
  );
  function offered(instanceId: string): boolean {
    return mySide?.offers.some((o) => o.instance_id === instanceId) ?? false;
  }

  const act = (run: () => Promise<unknown>): void => {
    setBusy(true);
    setError(null);
    run().then(
      () => { setBusy(false); onChanged(); },
      (err: unknown) => {
        setBusy(false);
        setError(err instanceof ApiError
          ? err
          : new ApiError('malformed_response', 0, 'Something unexpected went wrong.'));
      }
    );
  };

  const toggle = (instanceId: string): void => {
    const next = offered(instanceId)
      ? (mySide?.offers ?? []).filter((o) => o.instance_id !== instanceId)
      : [...(mySide?.offers ?? []), { instance_id: instanceId, to_uid: theirUid }];
    act(() => trades.setOffers(trade.id, next));
  };

  return (
    <Shell>
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">
            Trade with {trade.participants.filter((p) => p !== me).join(', ')}
          </h1>
          <p className="mt-1 font-mono text-xs text-ink-700">{trade.id}</p>
        </div>
        <Status status={trade.status} />
      </div>

      {error ? (
        <div className="mt-6">
          <Failed
            message={error.message}
            detail={error.fields.map((f) => f.message).join(' · ') || undefined}
          />
        </div>
      ) : null}

      {trade.status === 'settled' ? (
        <p className="mt-6 rounded-md border border-good-400/40 bg-good-400/5 p-3 text-sm text-good-400">
          Settled. Everything moved at once — check your collection.
        </p>
      ) : null}

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <section>
          <h2 className="text-sm font-medium uppercase tracking-wide text-ink-400">
            You give
            {mySide?.confirmed_at ? <Badge tone="good"> confirmed</Badge> : null}
          </h2>
          <Offers offers={mySide?.offers ?? []} />
        </section>

        {others.map((side) => (
          <section key={side.uid}>
            <h2 className="text-sm font-medium uppercase tracking-wide text-ink-400">
              {side.uid} gives
              {side.confirmed_at ? <Badge tone="good"> confirmed</Badge> : null}
            </h2>
            <Offers offers={side.offers} />
          </section>
        ))}
      </div>

      {!done ? (
        <>
          <section className="mt-10">
            <h2 className="text-sm font-medium uppercase tracking-wide text-ink-400">
              Your collection
            </h2>
            <p className="mt-1 text-xs text-ink-400">
              {/* Said before they edit, not after. */}
              Adding or removing anything clears every confirmation, including theirs.
            </p>
            {offerable.length === 0 ? (
              <p className="mt-3 text-sm text-ink-400">
                Nothing to offer. Only opened items can be traded.
              </p>
            ) : (
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {offerable.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => { toggle(item.id); }}
                      className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left ${
                        offered(item.id)
                          ? 'border-brand-400/50 bg-brand-500/10'
                          : 'border-ink-800 hover:border-ink-700'
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-mono text-xs">{item.reward_id}</span>
                        <span className="block truncate text-xs text-ink-700">
                          {item.channel_id}
                        </span>
                      </span>
                      {item.edition.number !== null ? (
                        <span className="tabular-nums text-ink-400">#{item.edition.number}</span>
                      ) : null}
                      {offered(item.id) ? <Badge tone="brand">Offered</Badge> : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Button
              disabled={busy || mySide?.confirmed_at !== null}
              // The HASH goes back with it. If anything changed since this
              // screen loaded, the server refuses rather than settling
              // something nobody agreed to.
              onClick={() => { act(() => trades.confirm(trade.id, trade.offer_hash)); }}
            >
              {mySide?.confirmed_at ? 'Confirmed' : 'Confirm this trade'}
            </Button>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => {
                act(async () => {
                  await trades.cancel(trade.id);
                  navigate(hrefFor(['trades']));
                });
              }}
            >
              Cancel the trade
            </Button>
            <span className="text-xs text-ink-700">
              Nothing moves until everyone has confirmed these exact contents.
            </span>
          </div>
        </>
      ) : null}
    </Shell>
  );
}

function Offers({ offers }: { offers: Trade['sides'][number]['offers'] }): ReactNode {
  if (offers.length === 0) {
    return <p className="mt-3 text-sm text-ink-400">Nothing yet.</p>;
  }
  return (
    <ul className="mt-3 divide-y divide-ink-800 rounded-lg border border-ink-800">
      {offers.map((o) => (
        <li key={o.instance_id} className="px-4 py-2 font-mono text-xs">{o.instance_id}</li>
      ))}
    </ul>
  );
}
